import React, { useEffect, useState } from "react";
import { PageHeader } from "components/ui";
import { Grid, Box } from "@mui/material";

import GriFormComponent from "components/customizedStandardsComponent/GriFormComponent";
import FreeTextInputComponent from "components/customizedStandardsComponent/FreeTextInputComponent";
import UploadEvidenceComponent from "components/customizedStandardsComponent/UploadEvidenceComponent";
import DownloadReport from "components/customizedStandardsComponent/DownloadReport";
import DownloadPostReviewGapForm from "components/customizedStandardsComponent/DownloadPostReviewGapForm";
import UploadPostReviewGapForm from "components/customizedStandardsComponent/UploadPostReviewGapForm";
import DownloadPostReviewReports from "components/customizedStandardsComponent/DownloadPostReviewReports";
import StandardsSelector from "components/customizedStandardsComponent/GriStandardsSelector";
import NavigationButtons from "helper/navigation/NavigationButtons";
import ReusableFormComponent from "components/customizedStandardsComponent/EsgFormComponent";
import UploadEvidenceReviewReports from "components/customizedStandardsComponent/UploadEvidenceReviewReports";
import ReportsReviewDialog from "components/customizedStandardsComponent/ReportsReviewDialog";

import {
  CLIMATE_IMPACT_ASSESSMENT_FORM,
  CLIMATE_IMPACT_SMART_DATA_FORM,
} from "data/forms/griFormSchema";

import { CLIMATE_IMPACT_STANDARDS } from "data/standards/griStandards";

import { FREE_TEXT_PROMPTS_DATA } from "data/prompts/freeTextPrompts";

import {
  generatedReportsAssClimateImpact,
  frameWorkandStandardMapping,
} from "data/reports/relevantReports";

const ClimateImpactAssesReporting = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [prevStep, setPrevStep] = useState(0);
  const [reportData, setReportData] = useState({
    selectedStandards: [],
    companyInfo: {},
  });
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);

  const steps = [
    "Select GRI Standards",
    "Organization & Company Information",
    "Free Text Input & Prompts",
    "Download Emissions Setup Guide",
    "Upload Evidence and Review Reports",
    "Enter Climate Impact Smart Data",
    "Download Climate Impact Reports",
    "Download Gap Form",
    "Upload Gap Form",
    "Download Post-Review Reports",
    "Post-Review Report Summary",
  ];

  const handleNext = () => {
    setPrevStep(activeStep);
    setActiveStep((prev) => prev + 1);
  };

  useEffect(() => {
    if (activeStep === 7 && prevStep === 6) {
      setShowReviewPopup(true);
    }
    setForceUpdate((prev) => prev + 1);
  }, [activeStep, prevStep]);

  const handleBack = () => {
    setPrevStep(activeStep);
    setActiveStep((prev) => prev - 1);
  };

  const handleSaveStandards = (selectedStandards) => {
    setReportData((prev) => ({
      ...prev,
      selectedStandards,
    }));
  };

  const handleClosePopup = () => {
    setShowReviewPopup(false);
  };

  const handleSkipToStep5 = () => {
    setPrevStep(activeStep);
    setActiveStep(8);
    setShowReviewPopup(false);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <StandardsSelector
            standards={CLIMATE_IMPACT_STANDARDS}
            onNext={handleNext}
            onSave={handleSaveStandards}
            title="Report Type Selection – What Do You Want to Generate?"
            nextButtonText="Next"
            initialSelectedStandards={reportData.selectedStandards.map(
              (s) => s.id
            )}
          />
        );
      case 1:
        return (
          <ReusableFormComponent
            {...CLIMATE_IMPACT_ASSESSMENT_FORM}
            onNext={handleNext}
            onPrevious={handleBack}
            showPreviousButton={true}
          />
        );

      case 2:
        return (
          <UploadEvidenceComponent
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 3:
        return (
          <FreeTextInputComponent
            stepData={FREE_TEXT_PROMPTS_DATA}
            onNext={handleNext}
            showSaveButton={true}
            showNextButton={true}
            titleColor="#02462E"
            subtitleColor="#02462E"
            formWidth={707}
          />
        );
      case 4:
        return (
          <DownloadReport
            key="emissions-setup-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={frameWorkandStandardMapping}
            title=""
            description="Download and review emissions tracking guidelines and frameworks"
            stepId="step-3"
            ReportDownloadButtonShow={false}
          />
        );
      case 5:
        return (
          <ReusableFormComponent
            {...CLIMATE_IMPACT_SMART_DATA_FORM}
            onNext={handleNext}
            onPrevious={handleBack}
            showPreviousButton={true}
            subtitle=""
          />
        );
      case 6:
        return (
          <DownloadReport
            key="climate-impact-reports-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={generatedReportsAssClimateImpact}
            showReportCountdownButton={true}
            title="Download Climate Impact Reports"
            description="Download and review generated climate impact reports"
            stepId="step-5"
          />
        );
      case 7:
        return (
          <UploadEvidenceReviewReports
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 8:
        return (
          <DownloadPostReviewGapForm
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 9:
        return (
          <UploadPostReviewGapForm
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 10:
        return (
          <DownloadPostReviewReports
            onPrevious={handleBack}
            standardName="Climate Impact"
            showReportCountdownButton={true}
          />
        );
      default:
        return <div>Unknown step 🥲</div>;
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <PageHeader title="AI-Powered ESG Reporting across Global, National, Sectoral, and Industry-Specific Standards" />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <NavigationButtons
              imageKey="dashboardImg2_2"
              title="Climate Impact"
              activeStep={activeStep}
              onBack={handleBack}
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          {getStepContent(activeStep)}
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 4,
              mb: 2,
            }}
          >
            {steps.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: index <= activeStep ? "#02462E" : "#e0e0e0",
                  mx: 1,
                }}
              />
            ))}
          </Box>
        </Grid>
      </Grid>

      <ReportsReviewDialog
        open={showReviewPopup}
        onClose={handleClosePopup}
        onSkip={handleSkipToStep5}
      />
    </>
  );
};

export default ClimateImpactAssesReporting;
